import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Bot, Send, Loader2, Sparkles, FileText } from 'lucide-react';

export default function AIAgentChat() {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const messagesEnd = useRef(null);

  useEffect(() => {
    api.get('/ai/chat/history').then(r => setMessages(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput('');
    setSending(true);

    // Optimistic add
    setMessages(prev => [...prev, { id: Date.now(), role: 'user', content: msg, timestamp: new Date().toISOString() }]);

    try {
      const res = await api.post('/ai/chat', { message: msg, context: 'general' });
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: res.data.response, timestamp: new Date().toISOString() }]);
    } catch (e) {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: 'Agent error. Please try again.', timestamp: new Date().toISOString() }]);
    }
    setSending(false);
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const projects = await api.get('/projects');
      if (projects.data.length > 0) {
        const res = await api.post(`/ai/daily-report/${projects.data[0].id}`);
        setMessages(prev => [...prev,
          { id: Date.now(), role: 'user', content: `Generate daily production report for ${projects.data[0].name}`, timestamp: new Date().toISOString() },
          { id: Date.now() + 1, role: 'assistant', content: res.data.report, timestamp: new Date().toISOString() }
        ]);
      }
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3rem)]" data-testid="ai-agent-chat">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-600/20 border border-blue-500/30 rounded-sm flex items-center justify-center">
            <Bot className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h1 className="text-lg font-heading font-bold text-white">VawworldOS — Head of Production</h1>
            <p className="text-[10px] text-zinc-500">Powered by GPT-5.2 | Online</p>
          </div>
        </div>
        <button
          data-testid="generate-report-button"
          onClick={generateReport}
          disabled={generating}
          className="h-8 px-4 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-xs font-medium rounded-sm transition-all flex items-center gap-2 disabled:opacity-50"
        >
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
          {generating ? 'Generating...' : 'Daily Report'}
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4 min-h-0">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-zinc-900 border border-zinc-800 rounded-sm flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-blue-500/50" />
            </div>
            <p className="text-sm text-zinc-400">VawworldOS — Head of Production</p>
            <p className="text-xs text-zinc-600 mt-1 max-w-sm">Ask about task status, deadlines, freelancer performance, or generate production reports.</p>
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              {['Show pipeline status', 'Who is overloaded?', 'Any deadline risks?', 'Generate daily report'].map(q => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-sm text-[11px] text-zinc-500 hover:text-zinc-200 hover:border-zinc-700 transition-all"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={msg.id || i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-message-${msg.role}`}>
            <div className={`max-w-2xl px-4 py-3 rounded-sm ${
              msg.role === 'user'
                ? 'bg-blue-600/20 border border-blue-500/20 text-blue-100'
                : 'bg-zinc-900 border border-zinc-800 text-zinc-300'
            }`}>
              {msg.role === 'assistant' && (
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Bot className="w-3 h-3 text-blue-400" />
                  <span className="text-[9px] text-blue-400 font-medium uppercase">VawworldOS</span>
                </div>
              )}
              <p className="text-xs whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              <p className="text-[9px] text-zinc-600 mt-1.5">{msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}</p>
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-3">
              <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
            </div>
          </div>
        )}

        <div ref={messagesEnd} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="pt-3 border-t border-zinc-800 relative z-10" data-testid="chat-input-form">
        <div className="flex gap-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask the production agent..."
            className="flex-1 h-10 bg-zinc-900 border border-zinc-800 rounded-sm px-4 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            data-testid="chat-send-button"
            type="submit"
            disabled={sending || !input.trim()}
            className="h-10 w-10 bg-blue-600 hover:bg-blue-500 rounded-sm flex items-center justify-center transition-all disabled:opacity-30 relative z-20"
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </form>
    </div>
  );
}
